import { FC } from 'react'
import { Alert, Typography } from '@material-tailwind/react'
import { PiChatCircleText } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'

import { useDarkMode } from '../contexts/DarkModeContext'
import { useProposalContext } from '../hooks/useProposalContext'
import { pendingSubmissionOf } from '../utils/helpers/agami.helpers'
import { formatDateString } from '../utils/helpers/dates.helpers'
import { AgamiSpot } from '../types/supabase'

/**
 * The banner at the HEAD of the edit form (05.5): whoever opens this form is
 * not editing their own content, they are DECIDING on someone else's proposal —
 * an information to read before touching any field, which is why it sits above
 * the form and not next to the title input.
 *
 * It shows the title AS PROPOSED (`original_title`): this is the ONLY place in
 * the interface where that value is visible, and the piece that makes the
 * revision comparable with what the visitor actually wrote. The data rides the
 * same satellite embed that feeds badges and counters (05.6) — the form makes
 * no query of its own on the satellite. Only the proposer's NAME travels
 * separately, through get-proposal-context, because `profiles` is readable by
 * nobody else.
 *
 * Renders nothing without a pending submission: an agami that is not a proposal
 * keeps its form identical to what it always was (AC-4 of the story).
 */
const ProposalBanner: FC<{ agami: AgamiSpot }> = ({ agami }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const submission = pendingSubmissionOf(agami)

  const { proposers } = useProposalContext({
    experienceId: submission?.experience_id,
    enabled: Boolean(submission),
  })

  if (!submission) return null

  const proposerName = submission.submitted_by ? proposers[submission.submitted_by] : undefined
  const date = formatDateString(submission.submitted_at)

  return (
    <Alert
      color="amber"
      variant={darkMode ? 'outlined' : 'ghost'}
      className="mb-4"
      icon={<PiChatCircleText className="h-6 w-6" />}
      data-testid="proposal-banner"
    >
      <Typography className="font-bold">{t('proposals.banner.title')}</Typography>
      <Typography variant="small">
        {proposerName
          ? t('proposals.banner.body', { title: submission.original_title, name: proposerName, date })
          : t('proposals.banner.bodyNoName', { title: submission.original_title, date })}
      </Typography>
    </Alert>
  )
}

export default ProposalBanner
